import type { BaseClient } from '../../http/BaseClient.js';
import { unwrapData } from '../../common/envelope.js';
import type {
  MagicLinkActivateRequest,
  MagicLinkSendResponse,
  MFASetupResponse,
  MFASimpleResponse,
  MFAStatus,
  MFAType,
  MFAVerifyLoginRequest,
  MFAVerifyLoginResponse,
  MFAVerifySetupRequest,
} from './types.js';

export class MfaClient {
  constructor(private readonly http: BaseClient) {}

  async getStatus(): Promise<MFAStatus> {
    const res = await this.http.get<{ success: boolean; data: MFAStatus }>('/mfa/status');
    return unwrapData(res);
  }

  async getMethods(): Promise<MFAType[]> {
    const res = await this.http.get<{ success: boolean; data: { methods: MFAType[] } }>('/mfa/methods');
    return res.data?.methods ?? [];
  }

  setup(body: { mfaType: MFAType; email?: string }): Promise<MFASetupResponse> {
    return this.http.post<MFASetupResponse>('/mfa/setup', { body });
  }

  verifySetup(body: MFAVerifySetupRequest): Promise<MFASimpleResponse> {
    return this.http.post<MFASimpleResponse>('/mfa/verify-setup', { body });
  }

  enable(mfaType: MFAType): Promise<MFASimpleResponse> {
    return this.http.post<MFASimpleResponse>('/mfa/enable', { body: { mfaType } });
  }

  disable(mfaType: MFAType): Promise<MFASimpleResponse> {
    return this.http.post<MFASimpleResponse>('/mfa/disable', { body: { mfaType } });
  }

  setPrimary(mfaType: MFAType): Promise<MFASimpleResponse> {
    return this.http.post<MFASimpleResponse>('/mfa/primary', { body: { mfaType } });
  }

  sendMagicLink(): Promise<MagicLinkSendResponse> {
    return this.http.post<MagicLinkSendResponse>('/mfa/magic-link/send', {});
  }

  activateMagicLink(body: MagicLinkActivateRequest): Promise<MFAVerifyLoginResponse> {
    return this.http.post<MFAVerifyLoginResponse>('/mfa/magic-link/activate', { body, skipAuth: true });
  }

  verifyLogin(body: MFAVerifyLoginRequest): Promise<MFAVerifyLoginResponse> {
    return this.http.post<MFAVerifyLoginResponse>('/mfa/verify-login', { body });
  }
}
